import { Box, Chip, Stack, Tooltip, Typography } from "@mui/material"
import { If } from "lib/@components/switch"
import { ASSIGNMENT_KIND_NAMES } from "../skill-mappings/schedule-assignments/assignment-kind-names"
import { shortFolderName } from "../skill-mappings/schedule-assignments/build-assignment-board"
import { round2 } from "../format-utils"
import { CostFigure } from "./cost-figure"

const CHIP_SX = { height: 18, fontSize: 11, fontWeight: 600, maxWidth: 1, alignSelf: "flex-start" }

/**
 * One row in the cost breakdown task table. When the resolver routed the task via a
 * conditional trade or a schedule assignment (FIQ-2457), a small chip under the trade says
 * which, and for an assignment whether it was a folder, schedule or task. Default-routed
 * tasks keep their existing look. Every cell can shrink and cuts its text short, so a long
 * title or chip never runs into the next column; the schedule code is never cut, so tasks
 * from schedules with the same title can still be told apart.
 */
export function VariantBreakdownTaskRow({ item, currency }) {
    const conditional = item.conditionMatched
    // FIQ-2457: set when a skill map schedule assignment routed the task
    const assignment = item.assignmentMatched
    const tradeLabel = item.tradeName || item.trade
    const assignmentLabel = describeAssignment(assignment)

    return (
        <Stack
            direction="row"
            alignItems="center"
            data-cy="variant-breakdown-task-row"
            sx={{ px: 2, py: 1, borderBottom: 1, borderColor: "divider", "&:last-child": { borderBottom: 0 } }}
        >
            <Stack direction="row" spacing={0.75} alignItems="baseline" sx={{ flex: 3, minWidth: 0, pr: 1 }}>
                <If value={!!item.scheduleCode}>
                    <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                        {item.scheduleCode}
                    </Typography>
                </If>
                <Tooltip title={item.title || ""}>
                    <Typography variant="body2" noWrap>
                        {item.title}
                    </Typography>
                </Tooltip>
            </Stack>
            <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0, pr: 1 }}>
                <Typography variant="body2" color="text.secondary" noWrap>
                    {tradeLabel}
                </Typography>
                <If value={!!conditional}>
                    <Tooltip title={`Routed by conditional: ${conditional?.name || ""}`}>
                        <Chip
                            label={conditional?.name || ""}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={CHIP_SX}
                            data-cy="task-conditional-chip"
                        />
                    </Tooltip>
                </If>
                <If value={!!assignment}>
                    <Tooltip title={`Routed by schedule assignment: ${assignmentLabel}`}>
                        <Chip
                            label={assignmentLabel}
                            size="small"
                            color="secondary"
                            variant="outlined"
                            sx={CHIP_SX}
                            data-cy="task-assignment-chip"
                        />
                    </Tooltip>
                </If>
            </Stack>
            <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
                <Typography variant="body2" color="text.secondary" noWrap>
                    {item.facilityName || item.facility}
                </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, textAlign: "right" }}>
                <Typography variant="body2" color="text.secondary">
                    {round2(item.annualHours)}h
                </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, textAlign: "right" }}>
                <CostFigure value={item.annualCost} currencySymbol={currency} fontWeight={500} />
            </Box>
        </Stack>
    )
}

// "Folder: Boilers", "Schedule: 22-11 Fire alarms", "Task: 22-11 · Monthly"
function describeAssignment(assignment) {
    if (!assignment) return ""
    const name = assignment.kind === "folder" ? shortFolderName(assignment.name) : assignment.name
    const kind = ASSIGNMENT_KIND_NAMES[assignment.kind]
    return kind ? `${kind}: ${name || ""}` : name || ""
}
